import { useState } from "react";
import "./CreditCardForm.css";
import { getCardProviderTags } from "./creditCardProviderTags";

const formatCardExpiry = (expiry) => {
  if (!expiry) {
    return "";
  }

  const date = new Date(expiry);
  if (Number.isNaN(date.getTime())) {
    return expiry;
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${year}`;
};

const formatSavedCardLabel = (card) => {
  const expiry = formatCardExpiry(card.exp);
  return expiry ? `${card.pan} · ${expiry}` : card.pan;
};

function DepositCreditCardForm({ onSubmit, userCards }) {
  const [view, setView] = useState(
    userCards && userCards.length > 0 ? "cardSelection" : "cardForm"
  );

  const [cardTokenData, setCardTokenData] = useState({
    card: (userCards && userCards[0]) || null,
    ccCvv: ""
  });

  const [cardData, setCardData] = useState({
    ccNumber: "",
    ccExpiryMonth: "",
    ccExpiryYear: "",
    ccCvv: "",
    ccHolderName: ""
  });

  const handleCardDataChange = (e) => {
    const { name, value } = e.target;
    setCardData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleCardTokenDataChange = (e) => {
    const { name, value } = e.target;
    setCardTokenData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmitSavedCard = async (e) => {
    e.preventDefault();

    console.log("Form submitted with token:", cardTokenData);
    if (typeof onSubmit === "function") {
      const result = await onSubmit(cardTokenData);

      const requiredAction = result?.requiredAction?.action;
      switch (requiredAction) {
        case "InsertCvv":
          break;
        case "InsertCardData":
          setView("cardForm");
          setCardData({
            ccNumber: "",
            ccExpiryMonth: "",
            ccExpiryYear: "",
            ccCvv: "",
            ccHolderName: ""
          });
          break;
        default:
      }
    }
  };

  const handleSubmitData = (e) => {
    e.preventDefault();

    console.log("Form submitted with data:", cardData);
    if (typeof onSubmit === "function") {
      onSubmit(cardData);
    }
  };

  const year = ((new Date().getFullYear() % 100) + 3).toString();
  const month = (12).toString();

  const renderCardForm = () => (
    <div className="credit-card-workspace">
      <form className="credit-card-form" onSubmit={handleSubmitData}>
        <div className="form-group">
          <label htmlFor="ccNumber">Card Number</label>
          <input
            type="text"
            id="ccNumber"
            name="ccNumber"
            value={cardData.ccNumber}
            onChange={handleCardDataChange}
            placeholder="Enter card number"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="ccExpiryMonth">Expiration Month</label>
          <input
            type="text"
            id="ccExpiryMonth"
            name="ccExpiryMonth"
            value={cardData.ccExpiryMonth}
            onChange={handleCardDataChange}
            placeholder="MM"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="ccExpiryYear">Expiration Year</label>
          <input
            type="text"
            id="ccExpiryYear"
            name="ccExpiryYear"
            value={cardData.ccExpiryYear}
            onChange={handleCardDataChange}
            placeholder="YYYY"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="ccCvv">CVV</label>
          <input
            type="password"
            id="ccCvv"
            name="ccCvv"
            inputMode="numeric"
            autoComplete="cc-csc"
            value={cardData.ccCvv}
            onChange={handleCardDataChange}
            placeholder="Enter CVV"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="ccHolderName">Cardholder Name</label>
          <input
            type="text"
            id="ccHolderName"
            name="ccHolderName"
            value={cardData.ccHolderName}
            onChange={handleCardDataChange}
            placeholder="Enter cardholder name"
            required
          />
        </div>

        <button className="primary-action" type="submit">
          Pay
        </button>
        <button
          className="secondary-action"
          onClick={() => setView("cardSelection")}
        >
          {"Back to Saved Cards"}
        </button>
      </form>

      {/* Test Data Buttons */}
      <div className="credit-card-form test-card-panel">
        <label htmlFor="test-data-viva">Viva cards</label>
        <button
          type="button"
          id="test-data-viva"
          onClick={() =>
            setCardData({
              ccNumber: "5374896606815395",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "Vlad Coceban"
            })
          }
        >
          5395 - Our personal test card
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4147463011110133",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          0133 - Visa
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4147463011110141",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          0141 - Error card
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5188340000000060",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          0060 - 3d-secured card
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5333308664112277",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          2277 - Also works on nuvei
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5101084411423750",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          3750 - Also works on nuvei
        </button>

        <small className="test-card-link">
          <a
            href="https://developer.viva.com/integration-reference/test-cards-and-environments/"
            target="_blank"
            rel="noreferrer"
          >
            View all test cards
          </a>
        </small>
        <hr />

        <label htmlFor="test-data-nuvei">Nuvei cards</label>
        <button
          id="test-data-nuvei"
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4001888687412469",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          2469 - Visa
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5101081046006034",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          6034 - Mastercard
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5333308664112277",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          2277 - Also works on viva
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5101084411423750",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          3750 - Also works on viva
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4000996174334475",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          4475 - Returns partial approval
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5333418445863914",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "John Doe"
            })
          }
        >
          3914 - Returns error
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4000020951595032",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "111",
              ccHolderName: "CL-BRW1"
            })
          }
        >
          {"5032 - 3d-secured card (need amount >= 150)"}
        </button>

        <small className="test-card-link">
          <a
            href="https://docs.nuvei.com/documentation/integration/testing/testing-cards/"
            target="_blank"
            rel="noreferrer"
          >
            View all test cards
          </a>
        </small>
        <hr />

        <label htmlFor="test-data-trust">Trust cards</label>
        <button
          id="test-data-trust"
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4111111111111111",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "123",
              ccHolderName: "John Doe"
            })
          }
        >
          1111 - Visa regular success
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5100000000000511",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "123",
              ccHolderName: "John Doe"
            })
          }
        >
          0511 - Mastercard regular success
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4900490000000667",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "123",
              ccHolderName: "John Doe"
            })
          }
        >
          0667 - Visa 3DS challenge
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "5591390000000645",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "123",
              ccHolderName: "John Doe"
            })
          }
        >
          0645 - Mastercard 3DS challenge
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4900490000000550",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "123",
              ccHolderName: "John Doe"
            })
          }
        >
          0550 - Visa challenge with method URL
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4900490000000501",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "123",
              ccHolderName: "John Doe"
            })
          }
        >
          0501 - Visa frictionless success
        </button>

        <button
          type="button"
          onClick={() =>
            setCardData({
              ccNumber: "4900490000000568",
              ccExpiryMonth: month,
              ccExpiryYear: year,
              ccCvv: "123",
              ccHolderName: "John Doe"
            })
          }
        >
          0568 - Visa failed challenge
        </button>

        <small className="test-card-link">
          <a
            href="https://help.trustpayments.com/hc/en-us/articles/6744062639121-Trust-Payments-3DS-Server-Testing-Resources"
            target="_blank"
            rel="noreferrer"
          >
            View all test cards
          </a>
        </small>
      </div>
    </div>
  );

  const renderCardSelection = () => (
    <form className="credit-card-form" onSubmit={handleSubmitSavedCard}>
      <label>{"Select from saved cards"}</label>
      <div id="saved_cards_container">
        {userCards
          .slice()
          .sort((a, b) => {
            if (!a.lastUsed && !b.lastUsed) return 0;
            if (!a.lastUsed) return 1;
            if (!b.lastUsed) return -1;
            return (
              new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime()
            );
          })
          .map((card, index) => {
            const providerTags = getCardProviderTags(card);

            return (
              <div className="saved-card-row" key={index}>
                <input
                  type="radio"
                  id={`card_${card.pan}-${card.exp}-${card.bin}`}
                  name="card"
                  value={`${card.pan}-${card.exp}-${card.bin}`}
                  checked={
                    cardTokenData.card &&
                    `${cardTokenData.card.pan}-${cardTokenData.card.exp}-${cardTokenData.card.bin}` ===
                      `${card.pan}-${card.exp}-${card.bin}`
                  }
                  onChange={() =>
                    handleCardTokenDataChange({
                      target: { name: "card", value: card }
                    })
                  }
                />
                <label htmlFor={`card_${card.pan}-${card.exp}-${card.bin}`}>
                  <span className="saved-card-text">
                    {formatSavedCardLabel(card)}
                  </span>
                  {providerTags.length > 0 && (
                    <span
                      className="saved-card-provider-tags"
                      aria-label="Card token providers"
                    >
                      {providerTags.map((provider) => (
                        <span
                          className="saved-card-provider-tag"
                          key={provider.id}
                        >
                          {provider.label}
                        </span>
                      ))}
                    </span>
                  )}
                </label>
              </div>
            );
          })}
      </div>
      <button className="primary-action" type="submit">
        Pay
      </button>
      <button className="secondary-action" onClick={() => setView("cardForm")}>
        {"Add New Card"}
      </button>
    </form>
  );

  return (
    <>{view === "cardSelection" ? renderCardSelection() : renderCardForm()}</>
  );
}

export default DepositCreditCardForm;
